import React, { useState } from 'react';

import { Modal, Title } from '@mantine/core';
import { useDebouncedValue, useDisclosure, useElementSize } from '@mantine/hooks';
import { IEventTypeMultiSelectAutoComplete } from 'components/form-fields/auto-complete-fields/EventTypeMultiSelectAutoComplete';
import { PackageCategoryAutoCompleteValue } from 'components/form-fields/auto-complete-fields/PackageCategoryAutoComplete';
import { IRevenueCategoryMultiSelectAutoComplete } from 'components/form-fields/auto-complete-fields/RevenueCategoryMultiSelectAutoComplete';
import { useAuth } from 'context/auth-context';
import { PackageListView } from 'types';

import DeletePackageModal from './components/DeletePackageModal';
import ArchivePackageModal from './components/PackageArchiveModal';
import PackageDrawer from './components/PackageDrawer';
import PackageDrawerState, { PackageType } from './components/PackageDrawer/types';
import PackagesPageHeader from './components/PackagesPageHeader';
import PackageTableContainer from './components/PackageTableContainer/PackageTableContainer';
import RevenueCategorizationDrawer from './components/RevenueCategorizationDrawer';
import SalesPortalOptionsDrawer from './components/SalesPortalOptionsDrawer';

const Package = (): JSX.Element => {
  const { hasPerm } = useAuth();
  const packagesPerm = hasPerm('PackageMutate');

  const { ref, height } = useElementSize();

  const [search, setSearch] = useState<string>('');
  const [eventTypes, setEventTypes] = useState<IEventTypeMultiSelectAutoComplete[]>([]);
  const [revenueCategories, setRevenueCategories] = useState<IRevenueCategoryMultiSelectAutoComplete[]>([]);
  const [packageCategory, setPackageCategory] = useState<PackageCategoryAutoCompleteValue | null>(null);
  const [packageTypes, setPackageTypes] = useState<PackageListView['type'][]>([]);
  const [isArchived, setArchived] = useState<boolean>(false);
  const [debouncedSearch] = useDebouncedValue<string>(search, 150);

  const [packageId, setPackageId] = useState<string>('');
  const [packageDrawerState, setPackageDrawerState] = useState<PackageDrawerState>('create-package');
  const [selectedPackageType, setSelectedPackageType] = useState<PackageType | undefined>();

  const [isPackageDrawerOpen, packageDrawerHandlers] = useDisclosure(false);
  const [isRevenueCategorizationDrawerOpen, revenueCategorizationDrawerHandlers] = useDisclosure(false);
  const [isSalesPortalOptionsOpen, salesPortalOptionsDrawerHandlers] = useDisclosure(false);
  const [isDeleteModalOpen, deleteModalHandlers] = useDisclosure(false);
  const [isArchiveModalOpen, archiveModalHandlers] = useDisclosure(false);

  const handleOpenPackageDrawer = (
    id: string,
    state: PackageDrawerState,
    packageType?: PackageType,
  ): void => {
    setPackageId(id);
    setPackageDrawerState(state);

    if (state === 'create-package') {
      setSelectedPackageType(undefined);
      setPackageDrawerState('select-type');
    }
    if (packageType) {
      setSelectedPackageType(packageType);
    }

    packageDrawerHandlers.open();
  };

  const handleOpenRevenueCategorizationDrawer = (id: string): void => {
    setPackageId(id);
    revenueCategorizationDrawerHandlers.open();
  };

  const handleOpenSalesPortalOptionsDrawer = (id: string): void => {
    setPackageId(id);
    salesPortalOptionsDrawerHandlers.open();
  };

  const handleOpenPackageDeleteModal = (id: string): void => {
    setPackageId(id);
    deleteModalHandlers.open();
  };

  const handleOpenArchivePackageModal = (id: string): void => {
    setPackageId(id);
    archiveModalHandlers.open();
  };

  return (
    <>
      <PackagesPageHeader
        search={search}
        setSearch={setSearch}
        eventTypes={eventTypes}
        packageCategory={packageCategory}
        setPackageCategory={setPackageCategory}
        revenueCategories={revenueCategories}
        setRevenueCategories={setRevenueCategories}
        packageTypes={packageTypes}
        setPackageTypes={setPackageTypes}
        setEventTypes={setEventTypes}
        isArchived={isArchived}
        setArchived={setArchived}
        handleOpenCreateForm={() => handleOpenPackageDrawer('', 'create-package')}
        permission={packagesPerm}
        headerRef={ref}
      />
      <PackageDrawer
        isOpen={isPackageDrawerOpen}
        closeDrawer={packageDrawerHandlers.close}
        state={packageDrawerState}
        setState={setPackageDrawerState}
        packageId={packageId}
        setPackageId={setPackageId}
        permission={packagesPerm}
        packageType={selectedPackageType}
        setPackageType={setSelectedPackageType}
      />
      <RevenueCategorizationDrawer
        isOpen={isRevenueCategorizationDrawerOpen}
        closeDrawer={() => {
          revenueCategorizationDrawerHandlers.close();
          setPackageId('');
        }}
        packageId={packageId}
        permission={packagesPerm && !isArchived}
      />
      <SalesPortalOptionsDrawer
        isOpen={isSalesPortalOptionsOpen}
        closeDrawer={() => {
          salesPortalOptionsDrawerHandlers.close();
          setPackageId('');
        }}
        packageId={packageId}
        permission={packagesPerm && !isArchived}
      />
      <PackageTableContainer
        search={debouncedSearch}
        eventTypes={eventTypes.map(v => v.eventTypeId)}
        packageCategory={packageCategory?.packageCategoryId}
        revenueCategories={revenueCategories.map(v => v.revenueCategoryId)}
        packageTypes={packageTypes}
        isArchived={isArchived}
        openPackageDrawer={handleOpenPackageDrawer}
        openRevenueCategorizationDrawer={handleOpenRevenueCategorizationDrawer}
        openSalesPortalOptionsDrawer={handleOpenSalesPortalOptionsDrawer}
        openPackageDeleteModal={handleOpenPackageDeleteModal}
        openArchivePackageModal={handleOpenArchivePackageModal}
        permission={packagesPerm}
        headerHeight={Math.round(height)}
      />
      <Modal
        opened={isDeleteModalOpen}
        onClose={deleteModalHandlers.close}
        title={<Title order={4}>Delete Package</Title>}
        trapFocus
        centered
        size='auto'
      >
        <DeletePackageModal packageId={packageId} handleClose={deleteModalHandlers.close} />
      </Modal>
      <ArchivePackageModal
        packageId={packageId}
        open={isArchiveModalOpen}
        handleClose={archiveModalHandlers.close}
      />
    </>
  );
};

export default Package;
